import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader } from 'src/modules/core/components'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { useBookingOnboarding, useUpdateBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useProfile } from '../../hooks/use-profile'
import { BookingAppointmentTypesSettings } from './booking-appointment-types-settings'
import { BookingAvailabilitySettings } from './booking-availability-settings'
import { BookingOnboardingIncomplete } from './booking-onboarding-incomplete'
import { BookingPageDetails } from './booking-page-details'
import classes from './settings-booking-page-tab.module.scss'

type BookingPageView = 'onboarding' | 'availability-settings' | 'appointment-types-settings'

export function SettingsBookingPageTab() {
  const { data: user } = useProfile()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const bookingOnboarding = useBookingOnboarding()
  const updateBookingOnboarding = useUpdateBookingOnboarding()
  const appliedEnableRescheduleRef = useRef(false)
  const [currentView, setCurrentView] = useState<BookingPageView>('onboarding')

  useEffect(() => {
    if (searchParams.get('enableReschedule') !== '1' || appliedEnableRescheduleRef.current) return
    appliedEnableRescheduleRef.current = true
    updateBookingOnboarding.mutate(
      { is_rescheduling_enabled: true },
      {
        onSettled: () => {
          navigate(`${routeNames.settings.path}?tab=booking-page`, { replace: true })
        },
      },
    )
  }, [searchParams, updateBookingOnboarding, navigate])

  const isLoading = useMemo(() => bookingOnboarding.isLoading, [bookingOnboarding.isLoading])

  if (isLoading) return <Loader isLoading={isLoading} />

  const isOnboardingComplete = bookingOnboarding.data?.onboarding_completed === true

  const handleShowAvailabilitySettings = () => {
    setCurrentView('availability-settings')
  }

  const handleShowAppointmentTypesSettings = () => {
    setCurrentView('appointment-types-settings')
  }

  const handleBackToOnboarding = () => {
    setCurrentView('onboarding')
  }

  return (
    <div className={classes.settingsWrapper}>
      <div className={classes.settingsWrapperChild}>
        {isOnboardingComplete ? (
          currentView === 'availability-settings' ? (
            <BookingAvailabilitySettings onBack={handleBackToOnboarding} />
          ) : currentView === 'appointment-types-settings' ? (
            <BookingAppointmentTypesSettings userId={user?.id} onBack={handleBackToOnboarding} />
          ) : (
            bookingOnboarding.data &&
            user && (
              <BookingPageDetails
                bookingOnboarding={bookingOnboarding.data}
                profile={user}
                onShowAvailabilitySettings={handleShowAvailabilitySettings}
                onShowAppointmentTypesSettings={handleShowAppointmentTypesSettings}
              />
            )
          )
        ) : currentView === 'availability-settings' ? (
          <BookingAvailabilitySettings onBack={handleBackToOnboarding} />
        ) : currentView === 'appointment-types-settings' ? (
          <BookingAppointmentTypesSettings userId={user?.id} onBack={handleBackToOnboarding} />
        ) : (
          bookingOnboarding.data && (
            <BookingOnboardingIncomplete
              onboardingData={bookingOnboarding.data}
              onShowAvailabilitySettings={handleShowAvailabilitySettings}
              onShowAppointmentTypesSettings={handleShowAppointmentTypesSettings}
            />
          )
        )}
      </div>
    </div>
  )
}
